import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  Building2, Car, ClipboardCheck, FileText, Gauge, Inbox, LayoutGrid, LogOut, Menu, Network,
  PenLine, Plane, Receipt, Settings, Sparkles, UserRound, UserCog, Users, UsersRound, Wallet, X,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { PLATFORM_NAME } from "@/lib/api";
import { AlertsBell } from "@/components/AlertsBell";
import { Assistant } from "@/components/Assistant";
import { HelpButton } from "@/components/HelpButton";
import { ConnectPaymentsBanner } from "@/components/ConnectPaymentsBanner";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/app", label: "Dashboard", icon: Gauge, testId: "nav-dashboard", end: true, perm: "reports" },
  { to: "/app/dispatch", label: "Dispatch", icon: LayoutGrid, testId: "nav-dispatch", perm: "dispatch", flag: "dispatch" },
  { to: "/app/inbox", label: "Requests", icon: ClipboardCheck, testId: "nav-inbox", perm: "dispatch", flag: "dispatch" },
  { to: "/app/manifests", label: "Trips", icon: FileText, testId: "nav-manifests", perm: "trips" },
  { to: "/app/airport-manifests", label: "Airport manifests", icon: Plane, testId: "nav-airport-manifests", perm: "trips" },
  { to: "/app/bookings", label: "Bookings", icon: Inbox, testId: "nav-bookings", perm: "trips" },
  { to: "/app/invoices", label: "Invoices", icon: Receipt, testId: "nav-invoices", perm: "payments", flag: "invoices" },
  { to: "/app/corporate", label: "Corporate accounts", icon: Building2, testId: "nav-corporate", perm: "payments", flag: "corporate" },
  { to: "/app/vehicles", label: "Vehicles", icon: Car, testId: "nav-vehicles", perm: "vehicles" },
  { to: "/app/chauffeurs", label: "Chauffeurs", icon: UserRound, testId: "nav-chauffeurs", perm: "vehicles" },
  { to: "/app/affiliates", label: "Affiliates", icon: Network, testId: "nav-affiliates", perm: "affiliates", flag: "affiliates" },
  { to: "/app/payouts", label: "Payouts & 1099", icon: Wallet, testId: "nav-payouts", perm: "payouts", flag: "reporting" },
  { to: "/app/community", label: "Community", icon: UsersRound, testId: "nav-community" },
  { to: "/app/blog", label: "Blog", icon: PenLine, testId: "nav-blog", perm: "settings", flag: "blog" },
  { to: "/app/team", label: "Portals & team", icon: Users, testId: "nav-team", ownerOnly: true },
  { to: "/app/settings", label: "Settings", icon: Settings, testId: "nav-settings", perm: "settings" },
  { to: "/app/profile", label: "Your account", icon: UserCog, testId: "nav-profile" },
  { to: "/app/plans", label: "Plans & Billing", icon: Sparkles, testId: "nav-plans", ownerOnly: true },
];

export const DashboardLayout = ({ children }) => {
  const { company, user, quota: allowance, logout, can, hasFlag } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [requests, setRequests] = useState(0);

  useEffect(() => {
    const count = () => api.get("/inbox/count")
      .then(({ data }) => setRequests(data.total || 0)).catch(() => {});
    count();
    window.addEventListener("fm:data-changed", count);
    return () => window.removeEventListener("fm:data-changed", count);
  }, [pathname]);

  const used = allowance?.used || 0;
  const quota = allowance?.limit
    ? (used >= allowance.limit
      ? `${used} trips · this week's limit reached`
      : `${used}/${allowance.limit} trips this week`)
    : `${used} trips this week · unlimited`;

  const linkCls = ({ isActive }) =>
    `flex items-center gap-3 rounded-lg px-3.5 py-2.5 text-sm transition-colors ${
      isActive ? "bg-gold/10 text-gold border border-gold/25" : "text-slate-400 hover:bg-ink-700 hover:text-slate-100"
    }`;

  const items = NAV.filter((i) =>
    (i.ownerOnly ? user?.role === "owner" : !i.perm || can(i.perm)) && (!i.flag || hasFlag(i.flag)));

  return (
    <div className="min-h-screen bg-ink-900 text-slate-200">
      <header className="glass-header sticky top-0 z-40 border-b border-ink-600">
        <div className="mx-auto flex h-16 max-w-[1600px] items-center justify-between gap-2 px-4 sm:px-6">
          <div className="flex min-w-0 items-center gap-3">
            <button data-testid="sidebar-toggle" className="lg:hidden shrink-0 text-slate-300" onClick={() => setOpen(!open)}>
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
            <Link to="/app" className="truncate font-display text-base font-semibold tracking-tight text-slate-100 sm:text-lg">
              {company?.name || PLATFORM_NAME}
            </Link>
            <span
              data-testid="plan-badge"
              className="hidden sm:inline-flex items-center rounded-full border border-gold/30 bg-gold/10 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-gold"
            >
              {company?.plan_name || "Free"}
            </span>
          </div>
          <div className="flex shrink-0 items-center gap-2 sm:gap-4">
            {!company?.suspended && (
              <span data-testid="quota-indicator" className="hidden font-mono text-xs text-slate-500 md:inline">
                {quota}
              </span>
            )}
            <AlertsBell />
            <Button
              data-testid="logout-btn"
              variant="ghost"
              size="sm"
              aria-label="Sign out"
              className="px-2 text-slate-400 hover:text-gold sm:px-3"
              onClick={async () => { await logout(); navigate("/"); }}
            >
              <LogOut className="h-4 w-4 sm:mr-2" /> <span className="hidden sm:inline">Sign out</span>
            </Button>
          </div>
        </div>
      </header>

      <div className="mx-auto flex max-w-[1600px]">
        <aside className={`${open ? "block" : "hidden"} lg:block w-full lg:w-64 shrink-0 border-r border-ink-600 p-4`}>
          <nav className="space-y-1.5">
            {items.map((item) => (
              <NavLink key={item.to} to={item.to} end={item.end} data-testid={item.testId}
                onClick={() => setOpen(false)} className={linkCls}>
                <item.icon className="h-4 w-4" /> {item.label}
                {item.to === "/app/inbox" && requests > 0 && (
                  <span data-testid="nav-inbox-badge"
                    className="ml-auto rounded-full bg-gold px-1.5 py-0.5 font-mono text-[10px] font-semibold text-ink-900">
                    {requests}
                  </span>
                )}
              </NavLink>
            ))}
          </nav>
          <div className="mt-8 rounded-lg border border-ink-600 bg-ink-800 p-4">
            <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
              Signed in · {user?.role}
            </p>
            <p className="mt-1.5 truncate text-sm text-slate-200">{user?.name}</p>
            <p className="truncate text-xs text-slate-500">{user?.email}</p>
          </div>
          {company?.show_powered_by && (
            <p data-testid="powered-by-badge" className="mt-4 rounded border border-ink-600 bg-ink-800 px-2.5 py-1.5 text-center font-mono text-[11px] text-slate-500">
              Powered by {PLATFORM_NAME}
            </p>
          )}
        </aside>

        <main className="min-w-0 flex-1 p-4 pb-32 sm:p-6 sm:pb-32 lg:p-8 lg:pb-10">
          {company?.suspended && pathname !== "/app/profile" ? (
            <div data-testid="suspended-notice" className="rounded-xl border border-red-500/40 bg-red-500/5 p-8">
              <p className="font-mono text-[11px] uppercase tracking-widest text-red-300">Account suspended</p>
              <p className="mt-3 text-lg text-slate-100">
                Your account has been suspended, so the office is read-locked for now.
              </p>
              <p className="mt-2 text-sm text-slate-400">
                {company.suspend_reason || "Please contact support to get this lifted."}
              </p>
              {user?.role === "owner" && (
                <Link to="/app/profile" data-testid="suspended-to-profile"
                  className="mt-5 inline-block font-mono text-xs uppercase tracking-widest text-gold">
                  Manage your account →
                </Link>
              )}
            </div>
          ) : (
            <>
              <ConnectPaymentsBanner />
              {children}
            </>
          )}
        </main>
      </div>
      <Assistant />
      <HelpButton app="office" />
    </div>
  );
};
